import { useState } from 'react';

import { Typography } from '@braskai/ui-kit';

import { Box, Stack } from '@mui/material';

import { IInvoicePreviewPayment } from '@/shared/api/billing/types';
import { getFullDate } from '@/shared/lib/dateTime';

import { InvoiceLineItem } from './InvoiceLineItem';
import { PromoCodeLine } from './PromoCodeLine';
import { VATLine } from './VATLine';

type FuturePaymentCardProps = {
  calculateVATPercentage: (taxAmount: number | undefined, totalAmount: number) => string;
  formatPrice: (amount: number, currency: string) => string;
  interval: string;
  payment: IInvoicePreviewPayment;
  promoCode?: null | string;
}

const calculatePaymentDiscount = (payment: IInvoicePreviewPayment): number => {
  return payment.items.reduce((total, item) => total + (item.discount_amount || 0), 0);
}

export const FuturePaymentCard = ({ calculateVATPercentage, formatPrice, interval, payment, promoCode }: FuturePaymentCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '8px',
        paddingY: '16px',
      }}
    >
      <Stack gap={16}>
        <Stack gap={4} paddingX={16}>
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Typography color="grey.900" variant="body-2-md">
              {interval === 'month' ? 'Monthly' : 'Yearly'} from{' '}
              {`${getFullDate(payment.date)[0]} ${getFullDate(payment.date)[1]}`}
            </Typography>
            <Typography color="grey.900" variant="body-2-md">
              {formatPrice(payment.amount, payment.currency)} /{interval === 'month' ? 'month' : 'year'}
            </Typography>
          </Stack>
          <Typography color="grey.500" variant="body-2">
            Renews automatically until cancelled
          </Typography>
        </Stack>

        <Typography
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            paddingX: 16,
            textAlign: 'left',
          }}
          color="grey.700"
          component="button"
          role="button"
          tabIndex={0}
          variant="caption-1-md"
          onClick={() => setIsExpanded(!isExpanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }
          }}
        >
          {isExpanded ? 'Hide details' : 'View details'}
        </Typography>

        {isExpanded && (
          <Stack
            sx={{
              borderTop: '1px solid',
              borderTopColor: 'grey.200',
              gap: 8,
              paddingTop: 16,
              paddingX: 16,
            }}
          >
            {payment.items.map((item) => (
              <InvoiceLineItem key={`${item.description}-${item.amount}`} formatPrice={formatPrice} item={item} />
            ))}
            <VATLine
              calculateVATPercentage={calculateVATPercentage}
              currency={payment.currency}
              formatPrice={formatPrice}
              taxAmount={payment.tax_amount}
              totalAmount={payment.amount}
            />
            {promoCode && (
              <PromoCodeLine
                code={promoCode}
                currency={payment.currency}
                discountAmount={calculatePaymentDiscount(payment)}
                formatPrice={formatPrice}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
